import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate, relDays } from "@/pages/Reperes";

export function MessageDialog({ ex, tf, onClose }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[440px]" data-testid="message-dialog">
        <DialogHeader><DialogTitle className="text-base">{tf("Message envoyé")}</DialogTitle></DialogHeader>
        {ex ? (
          <div className="text-xs space-y-2">
            <div className="text-muted-foreground">{fmtDate(ex.occurred_at)} · {relDays(ex.occurred_at, tf)}</div>
            <p className="rounded-lg border border-border bg-secondary/30 p-3 whitespace-pre-wrap text-foreground/90" data-testid="message-content">{ex.note || "—"}</p>
          </div>
        ) : <p className="text-xs text-muted-foreground">{tf("Aucun échange enregistré.")}</p>}
        <DialogFooter><Button variant="outline" size="sm" onClick={onClose}>{tf("Fermer")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ResponseDialog({ a, contacts, tf, onClose, onDone }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ channel: a.first_contact_channel || "whatsapp", interlocutor: contacts[0]?.id || "self", summary: "", outcome: "", recontact_date: "" });
  const [busy, setBusy] = useState(false);
  const [interested, setInterested] = useState(null); // athlete payload after 'interested'
  const submit = async () => {
    if (!form.outcome) { toast.error(tf("Choisissez une issue")); return; }
    setBusy(true);
    try {
      const r = await api.post(`/prises-de-contact/${a.id}/response`, {
        channel: form.channel, summary: form.summary, outcome: form.outcome,
        recontact_date: form.recontact_date || undefined,
        interlocutor_contact_id: form.interlocutor !== "self" ? form.interlocutor : undefined });
      if (form.outcome === "interested") setInterested(r.data.athlete);
      else { toast.success(tf("Réponse enregistrée")); onDone(); }
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  const toDiscussion = async () => {
    setBusy(true);
    try {
      await api.post(`/prises-de-contact/${a.id}/to-discussion`);
      toast.success(`${a.name} ${tf("est passé en discussion")}`);
      onDone();
      navigate("/discussions-actives");
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[440px]" data-testid="response-dialog">
        <DialogHeader><DialogTitle className="text-base">{tf("Enregistrer une réponse")} — {a.name}</DialogTitle></DialogHeader>
        {interested ? (
          <div className="space-y-3">
            <p className="text-sm">{tf("Réponse positive enregistrée. Passer cet athlète en discussion ?")}</p>
            <DialogFooter className="gap-1.5">
              <Button variant="outline" size="sm" onClick={onDone} data-testid="stay-contacted">{tf("Rester en prise de contact")}</Button>
              <Button size="sm" disabled={busy} className="bg-foreground hover:bg-foreground/90 text-background" onClick={toDiscussion} data-testid="to-discussion-confirm">{tf("Passer en discussion")}</Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2.5">
              <Select value={form.channel} onValueChange={(v) => setForm((f) => ({ ...f, channel: v }))}>
                <SelectTrigger className="h-9 text-xs" data-testid="response-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="phone">{tf("Téléphone")}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={form.interlocutor} onValueChange={(v) => setForm((f) => ({ ...f, interlocutor: v }))}>
                <SelectTrigger className="h-9 text-xs" data-testid="response-interlocutor"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  <SelectItem value="self">{a.name} ({tf("Athlète")})</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Textarea value={form.summary} onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
              placeholder={tf("Résumé de la réponse…")} className="text-xs min-h-[60px]" data-testid="response-summary" />
            <div>
              <label className="text-[11px] text-muted-foreground">{tf("Issue")}</label>
              <Select value={form.outcome} onValueChange={(v) => setForm((f) => ({ ...f, outcome: v }))}>
                <SelectTrigger className="h-9 text-xs" data-testid="response-outcome"><SelectValue placeholder={tf("Choisir…")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="interested">{tf("Intéressé")}</SelectItem>
                  <SelectItem value="later">{tf("À recontacter plus tard")}</SelectItem>
                  <SelectItem value="refused">{tf("Refus")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.outcome === "later" && (
              <div>
                <label className="text-[11px] text-muted-foreground" htmlFor="recontact-date">{tf("Recontacter le")}</label>
                <Input id="recontact-date" type="date" value={form.recontact_date} onChange={(e) => setForm((f) => ({ ...f, recontact_date: e.target.value }))} className="h-9 text-xs" data-testid="response-recontact-date" />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={onClose}>{tf("Annuler")}</Button>
              <Button size="sm" disabled={busy || !form.outcome} className="bg-foreground hover:bg-foreground/90 text-background" onClick={submit} data-testid="response-submit">{tf("Enregistrer")}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function FollowUpDialog({ a, tf, onClose, onDone }) {
  const [channel, setChannel] = useState(a.first_contact_channel || "whatsapp");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const n = Math.min((a.contact_attempts || 0) + 1, a.max_attempts || 3);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/prises-de-contact/${a.id}/follow-up`, { channel, note });
      toast.success(`${tf("Relance")} n°${n} ${tf("enregistrée")}`); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[400px]" data-testid="followup-dialog">
        <DialogHeader><DialogTitle className="text-base">{tf("Relancer")} — {a.name} ({n}/{a.max_attempts})</DialogTitle></DialogHeader>
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger className="h-9 text-xs" data-testid="followup-channel"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="phone">{tf("Téléphone")}</SelectItem>
          </SelectContent>
        </Select>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={tf("Note (optionnel)")} className="text-xs min-h-[60px]" data-testid="followup-note" />
        <p className="text-[11px] text-muted-foreground">{tf("La prochaine relance sera replanifiée automatiquement (J+5).")}</p>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>{tf("Annuler")}</Button>
          <Button size="sm" disabled={busy} className="bg-foreground hover:bg-foreground/90 text-background" onClick={submit} data-testid="followup-submit">{tf("Confirmer la relance")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ScheduleDialog({ a, tf, onClose, onDone }) {
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!date) { toast.error(tf("Date requise")); return; }
    setBusy(true);
    try {
      await api.post(`/prises-de-contact/${a.id}/schedule`, { date });
      toast.success(tf("Relance replanifiée")); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[340px]" data-testid="schedule-dialog">
        <DialogHeader><DialogTitle className="text-base">{tf("Modifier la date")} — {a.name}</DialogTitle></DialogHeader>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 text-xs" data-testid="schedule-date" />
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>{tf("Annuler")}</Button>
          <Button size="sm" disabled={busy || !date} className="bg-foreground hover:bg-foreground/90 text-background" onClick={submit} data-testid="schedule-submit">{tf("Enregistrer")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SnoozeDialog({ a, tf, onClose, onDone }) {
  const [days, setDays] = useState("90");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/prises-de-contact/${a.id}/snooze`, { days: parseInt(days, 10) });
      toast.success(tf("Mis en veille — un nouveau cycle s'ouvrira à l'échéance")); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[340px]" data-testid="snooze-dialog">
        <DialogHeader><DialogTitle className="text-base">{tf("Remettre en veille")} — {a.name}</DialogTitle></DialogHeader>
        <label className="text-[11px] text-muted-foreground">{tf("Recontacter dans")}</label>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="h-9 text-xs" data-testid="snooze-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="30">30 {tf("jours")}</SelectItem>
            <SelectItem value="60">60 {tf("jours")}</SelectItem>
            <SelectItem value="90">90 {tf("jours")}</SelectItem>
            <SelectItem value="180">180 {tf("jours")}</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-[11px] text-muted-foreground">{tf("L'athlète reste « Sans réponse » pendant la veille, puis repasse automatiquement en attente avec un compteur réinitialisé.")}</p>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>{tf("Annuler")}</Button>
          <Button size="sm" disabled={busy} className="bg-foreground hover:bg-foreground/90 text-background" onClick={submit} data-testid="snooze-submit">{tf("Mettre en veille")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ConfirmDialog({ tf, title, body, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[360px]" data-testid="confirm-dialog">
        <DialogHeader><DialogTitle className="text-base">{title}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">{body}</p>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>{tf("Annuler")}</Button>
          <Button size="sm" disabled={busy} className="bg-foreground hover:bg-foreground/90 text-background"
            onClick={async () => { setBusy(true); await onConfirm(); setBusy(false); }} data-testid="confirm-submit">{tf("Confirmer")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
